import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { sectionSchemaRegistry } from '@/lib/section-schema-registry'
import { cn } from '@/lib/utils'

import type { SectionElementSchema } from '@/types/element-schema.type'

interface StatItem {
  number: string
  label: string
}

interface StatsSectionProps {
  className?: string
  /** Section heading */
  heading?: string
  /** Section subheading */
  subheading?: string
  /** Heading text color */
  headingColor?: string
  /** Subheading text color */
  subheadingColor?: string
  /** Override default stats */
  stats?: StatItem[]
  /** Grid columns: 2, 3, or 4 */
  columns?: 2 | 3 | 4
  /** Background color */
  bgColor?: string
  /** Background gradient start color (overrides bgColor when both gradient colors set) */
  bgGradientFrom?: string
  /** Background gradient end color */
  bgGradientTo?: string
  /** Color of stat numbers */
  numberColor?: string
  /** Font size of stat numbers */
  numberSize?: string
  /** Color of stat labels */
  labelColor?: string
  /** Font size of stat labels */
  labelSize?: string
  /** Section padding class */
  padding?: string
  /** Section minimum height */
  minHeight?: string
}

const defaultStats: StatItem[] = [
  { number: '1,000+', label: 'Thành viên cộng đồng' },
  { number: '100%', label: 'Học viên hài lòng' },
  { number: '100%', label: 'Đối tác tuyển dụng tin tưởng' },
]

// Element schema for Puck editor
export const statsSectionSchema: SectionElementSchema = {
  componentType: 'StatsSection',
  label: 'Stats Section',
  category: 'content',
  description: 'Display statistics in a grid layout',
  elements: [
    {
      key: 'heading',
      type: 'textarea',
      label: 'Heading',
      helpText: 'Section title (supports HTML)',
      default: '',
    },
    {
      key: 'subheading',
      type: 'textarea',
      label: 'Subheading',
      helpText: 'Optional subtitle text',
      default: '',
    },
    {
      key: 'headingColor',
      type: 'color',
      label: 'Heading Color',
      default: '#111827',
    },
    {
      key: 'subheadingColor',
      type: 'color',
      label: 'Subheading Color',
      default: '#6b7280',
    },
    {
      key: 'stats',
      type: 'array',
      label: 'Statistics',
      helpText: 'Array of stat objects with number, label, and optional icon',
      default: defaultStats,
      children: [
        { key: 'number', type: 'text', label: 'Number/Value', required: true },
        { key: 'label', type: 'text', label: 'Label', required: true },
      ],
    },
    {
      key: 'columns',
      type: 'select',
      label: 'Grid Columns',
      options: [
        { label: '2 Columns', value: 2 },
        { label: '3 Columns', value: 3 },
        { label: '4 Columns', value: 4 },
      ],
      default: 3,
    },
    {
      key: 'bgColor',
      type: 'color',
      label: 'Background Color',
      helpText: 'Solid background color (ignored when gradient is set)',
      default: '#ffffff',
    },
    {
      key: 'bgGradientFrom',
      type: 'color',
      label: 'Background Gradient Start',
      helpText: 'Leave empty for solid background',
      default: '',
    },
    {
      key: 'bgGradientTo',
      type: 'color',
      label: 'Background Gradient End',
      helpText: 'Leave empty for solid background',
      default: '',
    },
    {
      key: 'numberColor',
      type: 'color',
      label: 'Number Color',
      helpText: 'Color of the stat numbers',
      default: '#dc2626',
    },
    {
      key: 'numberSize',
      type: 'select',
      label: 'Number Font Size',
      options: [
        { label: 'Small', value: 'text-2xl md:text-3xl' },
        { label: 'Medium', value: 'text-3xl md:text-4xl' },
        { label: 'Large (Default)', value: 'text-4xl md:text-5xl' },
        { label: 'Extra Large', value: 'text-5xl md:text-6xl' },
      ],
      default: 'text-4xl md:text-5xl',
    },
    {
      key: 'labelColor',
      type: 'color',
      label: 'Label Color',
      helpText: 'Color of the stat labels',
      default: '#374151',
    },
    {
      key: 'labelSize',
      type: 'select',
      label: 'Label Font Size',
      options: [
        { label: 'Small', value: 'text-sm md:text-base' },
        { label: 'Medium (Default)', value: 'text-base md:text-lg' },
        { label: 'Large', value: 'text-lg md:text-xl' },
      ],
      default: 'text-base md:text-lg',
    },
    {
      key: 'padding',
      type: 'select',
      label: 'Section Padding',
      options: [
        { label: 'Small', value: 'py-8 px-4' },
        { label: 'Medium', value: 'py-12 px-4' },
        { label: 'Large (Default)', value: 'py-16 px-4' },
        { label: 'Extra Large', value: 'py-24 px-4' },
      ],
      default: 'py-16 px-4',
    },
    {
      key: 'minHeight',
      type: 'select',
      label: 'Minimum Height',
      options: [
        { label: 'Auto', value: 'auto' },
        { label: 'Small', value: 'min-h-[20vh]' },
        { label: 'Medium', value: 'min-h-[40vh]' },
        { label: 'Large', value: 'min-h-[60vh]' },
      ],
      default: 'auto',
    },
    {
      key: 'className',
      type: 'text',
      label: 'CSS Class',
      helpText: 'Additional CSS classes',
    },
  ],
}

// Register schema
sectionSchemaRegistry.register(statsSectionSchema)

export default function StatsSection({
  className,
  heading = '',
  subheading = '',
  headingColor = '#111827',
  subheadingColor = '#6b7280',
  stats = defaultStats,
  columns = 3,
  bgColor = '#ffffff',
  bgGradientFrom = '',
  bgGradientTo = '',
  numberColor = '#dc2626',
  numberSize = 'text-4xl md:text-5xl',
  labelColor = '#374151',
  labelSize = 'text-base md:text-lg',
  padding = 'py-16 px-4',
  minHeight = 'auto',
}: StatsSectionProps) {
  const gridCols = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
  }

  const hasGradient = bgGradientFrom && bgGradientTo
  const backgroundStyle: React.CSSProperties = hasGradient
    ? { background: `linear-gradient(to bottom, ${bgGradientFrom}, ${bgGradientTo})` }
    : { backgroundColor: bgColor }

  return (
    <section className={cn('w-full', padding, minHeight !== 'auto' && minHeight, className)} style={backgroundStyle}>
      <div className="max-w-6xl mx-auto">
        {heading && (
          <div className="text-center mb-10">
            <h2
              className="text-3xl md:text-4xl font-bold mb-3"
              style={{ color: headingColor }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(heading) }}
            />
            {subheading && (
              <p
                className="text-base md:text-lg"
                style={{ color: subheadingColor }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(subheading) }}
              />
            )}
          </div>
        )}
        <div className={cn('grid grid-cols-1 gap-8 md:gap-12', gridCols[columns])}>
          {stats.map((stat, index) => (
            <div key={index} className="flex flex-col items-center justify-center text-center">
              <div className={cn(numberSize, 'font-bold mb-3')} style={{ color: numberColor }}>
                {stat.number}
              </div>
              <p className={cn(labelSize, 'font-medium')} style={{ color: labelColor }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
